import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
    Eye,
    EyeOff,
    User,
    Lock,
    Mail,
    UserPlus,
    ArrowRight,
    CheckCircle,
    Zap,
    Globe
} from 'lucide-react';
import APIRequests from '../../../utility/APIRequests';
import Helper1, { notify } from '../../../utility/Helper1';
import { useSelector, useDispatch } from 'react-redux';
import { addUrlToPath } from '../../../utility/Helper1'
import { userSelect, allRemoteSettingsSelect } from '../../../redux/selectors/index';
import { authActions } from '../../../redux/actions';

import { WebLink, SiteWide, Images } from "../../../utility/customComponents";
import { AiFillCloseCircle } from "react-icons/ai";

const AuthHolder = (props) => {
    const navigate = useNavigate();
    const location = useLocation();
    const site_settings = useSelector(allRemoteSettingsSelect);

    // Determine active tab based on current route
    const getActiveTab = () => {
        const path = location.pathname.toLowerCase();
        if (path.includes('signup') || path.includes('register')) {
            return 'signup';
        }
        return 'signin';
    };

    const [activeTab, setActiveTab] = useState(getActiveTab());

    // Update tab when route changes
    useEffect(() => {
        setActiveTab(getActiveTab());
    }, [location.pathname]);

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        // Navigate to appropriate route
        if (tab === 'signup') {
            navigate('/signup', { replace: true });
        } else {
            navigate('/signin', { replace: true });
        }
    };

    return (
        <SiteWide pageTitle={props.title} themeColor={"#01131d"}>
            <div className="min-h-screen bg-gradient-to-tr from-slate-900 via-gray-900 to-black flex items-center justify-center p-6 relative overflow-hidden">

                {/* Sophisticated Background Pattern */}
                <div className="absolute inset-0">
                    <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-amber-500/5 via-transparent to-yellow-600/5"></div>
                    <div className="absolute top-1/3 right-0 w-96 h-96 bg-gradient-to-l from-amber-400/10 to-transparent rounded-full blur-3xl"></div>
                    <div className="absolute bottom-0 left-1/4 w-80 h-80 bg-gradient-to-r from-yellow-500/8 to-amber-400/8 rounded-full blur-2xl"></div>

                    {/* Geometric patterns */}
                    <div className="absolute top-20 left-20 w-32 h-32 border border-amber-400/10 rotate-45 rounded-3xl"></div>
                    <div className="absolute bottom-32 right-32 w-24 h-24 border border-yellow-500/10 rotate-12 rounded-2xl"></div>
                </div>

                <div className="relative z-10 w-full max-w-6xl flex items-center justify-center">

                    {/* Left Side - Brand/Hero Section */}
                    <div className="hidden lg:flex lg:w-1/2 flex-col justify-center pr-16">
                        <div className="space-y-8">
                            <div className="space-y-4">
                                <div className="inline-flex items-center space-x-3">
                                    <div className="p-3 bg-gradient-to-r from-green-400 to-emerald-500 rounded-2xl">
                                        <img style={{height: 60}} src="/logo192.png" />
                                    </div>
                                    {/* <h1 className="text-4xl font-bold bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                                        Elevate
                                    </h1> */}
                                </div>
                                <h2 className="text-5xl font-extrabold leading-tight">
                                    <span className="bg-gradient-to-r from-green-400 via-emerald-500 to-green-600 bg-clip-text text-transparent">
                                        Empower
                                    </span>
                                    <br />
                                    <span className="text-white">Your Financial</span>
                                    <br />
                                    <span className="text-gray-300">Freedom</span>
                                </h2>
                            </div>

                            <p className="text-xl text-gray-400 leading-relaxed max-w-md">
                                Discover smarter investing with our peer-to-peer platform. Connect directly with vetted peer and earn competitive returns—securely and transparently.
                            </p>

                            <div className="space-y-4">
                                <div className="flex items-center space-x-3 text-gray-300">
                                    <CheckCircle className="text-green-400" size={20} />
                                    <span>Secure P2P Transactions</span>
                                </div>
                                <div className="flex items-center space-x-3 text-gray-300">
                                    <CheckCircle className="text-green-400" size={20} />
                                    <span>Transparent Assessment</span>
                                </div>
                                <div className="flex items-center space-x-3 text-gray-300">
                                    <CheckCircle className="text-green-400" size={20} />
                                    <span>Instant Payouts</span>
                                </div>
                            </div>
                        </div>
                    </div>


                    {/* Right Side - Auth Form */}
                    <div className="w-full lg:w-1/2 max-w-md">

                        {/* Modern Floating Tab Selector */}
                        <div className="mb-8">
                            <div className="relative bg-gray-800/30 backdrop-blur-xl rounded-full p-1.5 border border-gray-700/50">
                                <div
                                    className={`absolute top-1.5 bottom-1.5 bg-gradient-to-r from-amber-400 to-yellow-500 rounded-full transition-all duration-500 ease-out ${activeTab === 'signin' ? 'left-1.5 right-1/2 mr-0.5' : 'right-1.5 left-1/2 ml-0.5'
                                        }`}
                                ></div>
                                <div className="relative flex">
                                    <button
                                        onClick={() => handleTabChange('signin')}
                                        className={`flex-1 py-4 px-8 rounded-full font-semibold transition-all duration-300 text-center ${activeTab === 'signin'
                                                ? 'text-black relative z-10'
                                                : 'text-gray-300 hover:text-white'
                                            }`}
                                    >
                                        Access Account
                                    </button>
                                    <button
                                        onClick={() => handleTabChange('signup')}
                                        className={`flex-1 py-4 px-8 rounded-full font-semibold transition-all duration-300 text-center ${activeTab === 'signup'
                                                ? 'text-black relative z-10'
                                                : 'text-gray-300 hover:text-white'
                                            }`}
                                    >
                                        Create Account
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Main Form Card */}
                        <div className="bg-gray-900/60 backdrop-blur-2xl border border-gray-700/50 rounded-3xl p-8 shadow-2xl">

                            {/* Dynamic Header */}
                            {!props?.skipHeader && <div className="text-center mb-8">
                                <div className="inline-flex items-center justify-center w-14 h-14 bg-gradient-to-r from-amber-400/20 to-yellow-500/20 rounded-2xl mb-4 border border-amber-400/30">
                                    {activeTab === 'signin' ?
                                        <User className="text-amber-400" size={24} /> :
                                        <UserPlus className="text-amber-400" size={24} />
                                    }
                                </div>
                                <h3 className="text-2xl font-bold text-white mb-2">
                                    {activeTab === 'signin' ? 'Continue Access' : 'Begin Your Trade'}
                                </h3>
                                <p className="text-gray-400">
                                    {activeTab === 'signin'
                                        ? 'Enter your credentials to proceed'
                                        : 'Fill in your details to get started'
                                    }
                                </p>
                            </div>}

                            {/* Dynamic Form Content */}
                            <div className="space-y-6">
                                {/* Pass activeTab and handleTabChange to children via React.cloneElement */}
                                {React.Children.map(props.children, child => {
                                    if (React.isValidElement(child)) {
                                        return React.cloneElement(child, {
                                            activeTab,
                                            setActiveTab: handleTabChange
                                        });
                                    }
                                    return child;
                                })}
                            </div>
                        </div>

                        {/* Bottom Info */}
                        <div className="mt-6 text-center">
                            <div className="flex items-center justify-center space-x-2 text-gray-500 text-sm">
                                <Globe size={16} className="text-amber-400/60" />
                                <span>Data Protection Assured</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </SiteWide>
    );
};

export default AuthHolder;